import Hoverable from '@omni/kit/components/Hoverable';
import KitBadge from '@omni/kit/components/KitBadge';
import { BadgeProps } from '@omni/kit/components/KitBadge/KitBadge';
import KitIcon from '@omni/kit/components/KitIcon';
import { IconName } from '@omni/kit/components/KitIcon';
import Tooltip, { TooltipProps } from '@omni/kit/components/Tooltip';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { transparentHexColor } from '@omni/kit/utilities/utilities';
import React, { ReactElement, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View, ViewStyle } from 'react-native';

export enum PlayerButtonSizes {
  small = 'small',
  medium = 'medium',
  large = 'large',
}

const iconSizeMap = Platform.select({
  web: {
    [PlayerButtonSizes.small]: 20,
    [PlayerButtonSizes.medium]: 32,
    [PlayerButtonSizes.large]: 64,
  },
  default: {
    [PlayerButtonSizes.small]: 12,
    [PlayerButtonSizes.medium]: 20,
    [PlayerButtonSizes.large]: 24,
  },
});

const touchableOffsetMap = Platform.select({
  web: {
    [PlayerButtonSizes.small]: 12,
    [PlayerButtonSizes.medium]: 16,
    [PlayerButtonSizes.large]: 56,
  },
  default: {
    [PlayerButtonSizes.small]: 18,
    [PlayerButtonSizes.medium]: 24,
    [PlayerButtonSizes.large]: 30,
  },
});

const HoverColor = transparentHexColor(Colors.N0, 20);
const PressColor = transparentHexColor(Colors.N0, 15);
const FocusColor = transparentHexColor(Colors.N0, 90);

export interface PlayerButtonProps {
  frameless?: boolean;
  icon: IconName;
  onBlur?: () => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
  onLayout?: (event: any) => void;
  onPress?: () => void;
  onPressIn?: () => void;
  onPressOut?: () => void;
  size?: PlayerButtonSizes;
  style?: ViewStyle;
  tooltipProps?: Omit<TooltipProps, 'children'>;
  badge?: boolean;
  badgeProps?: BadgeProps;
}

/**
 * Standard media player button component
 *
 * Description:
 * - Handles hover and press states by scaling and applying opacity
 *
 * Note: `onFocus` will prevent `onPressIn` and `onPressOut` events
 * from being triggered when the button is pressed
 */
export default function PlayerButton({
  frameless = false,
  icon,

  onBlur,
  onHoverIn,
  onHoverOut,
  onLayout,
  onPress,
  onPressIn,
  onPressOut,
  size = PlayerButtonSizes.medium,
  style = {},
  tooltipProps,
  badge = false,
  badgeProps = {
    color: 'default',
    size: 'm',
    style: {},
    text: '',
    textStyle: {},
  },
}: PlayerButtonProps): ReactElement {
  const playerButtonRef = useRef<View>(null);

  const [isHovering, setIsHovering] = useState(false);
  const [isPressing, setIsPressing] = useState(false);

  const iconSize = iconSizeMap[size];
  const touchableOffset = touchableOffsetMap[size];

  const buttonContent = (
    <Hoverable
      onHoverIn={() => {
        setIsHovering(true);
        onHoverIn?.();
      }}
      onHoverOut={() => {
        setIsHovering(false);
        onHoverOut?.();
      }}
    >
      <View style={style}>
        <Pressable
          ref={playerButtonRef}
          onPress={onPress}
          onPressIn={() => {
            setIsPressing(true);
            onPressIn?.();
          }}
          onPressOut={() => {
            setIsPressing(false);
            onPressOut?.();
          }}
          onBlur={onBlur}
          onLayout={onLayout}
          style={() => [
            styles.button,
            !frameless && styles.defaultColor,

            isHovering && styles.scaleUp,
            isHovering && frameless && styles.framelessHoverColor,

            isPressing && styles.scaleDown,
            isPressing && frameless && styles.framelessPressColor,
            {
              outlineStyle: (Platform.OS === 'web' ? 'none' : undefined) as
                | 'solid'
                | 'dotted'
                | 'dashed'
                | undefined,
              height: iconSize + touchableOffset * 2,
              width: iconSize + touchableOffset * 2,
            },
          ]}
        >
          {badge ? <KitBadge {...badgeProps} /> : null}
          <KitIcon
            name={icon}
            color={frameless ? Colors.N0 : Colors.N1000}
            size={iconSize}
            style={isPressing && !frameless ? styles.framedPressColor : {}}
          />
        </Pressable>
      </View>
    </Hoverable>
  );

  return tooltipProps?.label ? (
    <Tooltip offsetY={Spacing.xs} keepTooltipInside={true} {...tooltipProps}>
      {buttonContent}
    </Tooltip>
  ) : (
    buttonContent
  );
}

const styles = StyleSheet.create({
  button: {
    borderRadius: BorderRadius.full,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  defaultColor: {
    backgroundColor: Colors.N0,
  },
  framelessHoverColor: {
    backgroundColor: HoverColor,
  },
  framelessPressColor: {
    backgroundColor: PressColor,
  },
  framedPressColor: {
    opacity: 0.8,
  },
  scaleDown: {
    transform: [{ scale: 0.98 }],
  },
  scaleUp: {
    transform: [{ scale: 1.02 }],
  },
  buttonFocused: {
    backgroundColor: FocusColor,
  },
});
